import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import moment from 'moment-timezone';
import { currentBrowserTimeZone } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { roundToStep } from 'utils/utils';

const CustomMessageScheduleOverlayPanel = React.forwardRef(
  ({ visible = false, onHide, style = {}, scheduleData, setScheduleData }, ref) => {
    const userContext = useUserDetailsContext();
    const timezone = userContext?.userDetails?.timezone || currentBrowserTimeZone;

    // Store Date objects from Calendar component (local/browser timezone)
    const [customDate, setCustomDate] = useState(null);
    const [customTime, setCustomTime] = useState(null);
    const [error, setError] = useState(false);
    const [dateTimeError, setDateTimeError] = useState(false);
    const [selectedQuick, setSelectedQuick] = useState(null);

    // Helper function to create datetime for "In 4 hours" in user's timezone
    const getIn4HoursDateTime = useCallback(() => {
      return moment().tz(timezone).add(4, 'hours').format();
    }, [timezone]);

    // Helper function to create datetime for "Tomorrow 9AM" in user's timezone
    const getTomorrow9AMDateTime = useCallback(() => {
      return moment().tz(timezone).add(1, 'day').startOf('day').hour(9).format();
    }, [timezone]);

    /**
     * Combine selected date and time into ISO string with user's timezone
     * Calendar component returns Date objects in local/browser timezone
     * We interpret these as being in the user's selected timezone
     */
    const getCustomDateTime = useCallback(() => {
      if (!customDate || !customTime) return null;

      // Calendar returns Date objects in local/browser timezone
      // We interpret these Date objects as being in the user's timezone
      const dateInUserTimezone = moment.tz(
        {
          year: customDate.getFullYear(),
          month: customDate.getMonth(),
          day: customDate.getDate(),
          hour: customTime.getHours(),
          minute: customTime.getMinutes(),
          second: 0,
        },
        timezone
      );

      // Return ISO string with timezone information
      return dateInUserTimezone.format();
    }, [customDate, customTime, timezone]);

    /**
     * Validate if selected date/time is in the future
     * Date and time are Date objects from Calendar component (local/browser timezone)
     * We validate them in the user's selected timezone
     */
    const validateDateTime = useCallback(
      (date, time) => {
        if (!date || !time) return true;

        // Create moment in user's timezone using the selected date/time parts
        const selectedDateTimeInUserTimezone = moment.tz(
          {
            year: date.getFullYear(),
            month: date.getMonth(),
            day: date.getDate(),
            hour: time.getHours(),
            minute: time.getMinutes(),
            second: 0,
          },
          timezone
        );

        // Compare with current time in user's timezone
        const currentTimeInUserTimezone = moment().tz(timezone);
        return selectedDateTimeInUserTimezone.isAfter(currentTimeInUserTimezone);
      },
      [timezone]
    );

    // Restore state from scheduleData
    useEffect(() => {
      if (scheduleData?.datetime) {
        try {
          if (scheduleData.type === 'custom') {
            // Parse the stored datetime and convert to user's timezone for Calendar display
            const parsedDateTime = moment(scheduleData.datetime);

            // Create separate Date objects for date and time parts in local/browser time
            // Calendar component expects Date objects in local/browser timezone
            const dateObj = new Date(parsedDateTime.year(), parsedDateTime.month(), parsedDateTime.date());
            const timeObj = new Date(
              parsedDateTime.year(),
              parsedDateTime.month(),
              parsedDateTime.date(),
              parsedDateTime.hour(),
              parsedDateTime.minute(),
              parsedDateTime.second()
            );

            setCustomDate(dateObj);
            setCustomTime(timeObj);
            setSelectedQuick(null);
          } else if (scheduleData.type === 'quick') {
            setCustomDate(null);
            setCustomTime(null);
            setSelectedQuick(scheduleData.label || null);
          }
        } catch (err) {
          console.error('Error parsing schedule datetime:', err);
        }
      } else {
        setCustomDate(null);
        setCustomTime(null);
        setSelectedQuick(null);
      }
    }, [scheduleData]);

    // Validate date/time whenever they change
    useEffect(() => {
      if (customDate && customTime) {
        const isValid = validateDateTime(customDate, customTime);
        setDateTimeError(!isValid);
      } else {
        setDateTimeError(false);
      }
    }, [customDate, customTime]);

    const onSchedule = (value) => {};

    const schedule = (value) => {
      let scheduleData = null;

      if (value === 'In 4 hours') {
        scheduleData = {
          datetime: getIn4HoursDateTime(),
          type: 'quick',
          label: 'In 4 hours',
        };
      } else if (value === 'Tomorrow 9AM') {
        scheduleData = {
          datetime: getTomorrow9AMDateTime(),
          type: 'quick',
          label: 'Tomorrow 9AM',
        };
      } else if (value && typeof value === 'object' && value?.type === 'custom') {
        if (!customDate || !customTime) {
          setError(true);
          return;
        }

        // Check if date/time is in the future
        if (!validateDateTime(customDate, customTime)) {
          setDateTimeError(true);
          return;
        }

        scheduleData = {
          datetime: getCustomDateTime(),
          type: 'custom',
          label: 'Custom',
        };
      }

      if (scheduleData) {
        if (setScheduleData) {
          setScheduleData(scheduleData);
        }
        if (onSchedule) onSchedule(scheduleData);
        if (onHide) onHide();
        setError(false);
        setDateTimeError(false);
      }
    };

    const clearSchedule = (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      setCustomDate(null);
      setCustomTime(null);
      setError(false);
      setDateTimeError(false);
      if (setScheduleData) {
        setScheduleData(null);
      }
    };

    if (!visible) return null;

    return (
      <div ref={ref} className="scheduled-msg-panel p-3">
        <div>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 p-0">
            <span className="fw-semibold fs-5 black-800">Schedule for later</span>
            <div className="d-flex align-items-center p-0">
              <Button
                label="Clear"
                className="p-button-text text-primary p-0 fs-6 fw-normal bg-transparent border-0"
                onClick={clearSchedule}
              />
              <i className="bi bi-x-lg ms-3 cursor-pointer" onClick={onHide}></i>
            </div>
          </div>

          <div className="d-flex gap-3 mb-3 p-0">
            <Button
              label="In 4 hours"
              className={`${
                selectedQuick === 'In 4 hours' ? ' flex-1 schedule-option-btn selected-option' : ' flex-1 schedule-option-btn'
              }`}
              onClick={() => schedule('In 4 hours')}
            />
            <Button
              label="Tomorrow 9AM"
              className={`${
                selectedQuick === 'Tomorrow 9AM' ? ' flex-1 schedule-option-btn selected-option' : ' flex-1 schedule-option-btn '
              }`}
              onClick={() => schedule('Tomorrow 9AM')}
            />
          </div>
          {/* Custom Date/Time */}
          <div
            className={`${
              customDate && customTime && !dateTimeError
                ? ' p-3 border input-border bg-transparent rounded-3 schedule-option-btn selected-option'
                : ' p-3 border input-border bg-transparent rounded-3 schedule-option-btn'
            }`}
          >
            <div className="mb-2">
              <span className="fw-semibold fs-6 black-800">Custom Date and Time</span>
            </div>

            <div className="d-flex gap-3 m-0 p-0">
              <div className="flex-1 p-0">
                <Calendar
                  value={customDate}
                  minDate={new Date()}
                  onChange={(e) => {
                    setCustomDate(e.value);
                    setSelectedQuick(null);
                    setError(false);
                  }}
                  placeholder="Select Date"
                  className={`w-full rounded-2 ${
                    (error && !customDate) || dateTimeError ? 'border border-danger' : 'border border-light'
                  } fs-6 text-dark bg-white`}
                  panelStyle={{ borderRadius: '8px' }}
                  monthNavigator
                  yearNavigator
                  yearRange="2020:2030"
                />
              </div>
              <div className="flex-1 p-0">
                <Calendar
                  value={customTime}
                  onChange={(e) => {
                    setCustomTime(e.value);
                    setSelectedQuick(null);
                    setError(false);
                  }}
                  onSelect={(e) => {
                    setCustomTime(roundToStep(e.value));
                  }}
                  timeOnly
                  hourFormat="12"
                  stepMinute={15}
                  placeholder="Select Time"
                  className={`w-full rounded-2 ${
                    (error && !customTime) || dateTimeError ? 'border border-danger' : 'border border-light'
                  } fs-6 text-dark bg-white`}
                  panelStyle={{ borderRadius: '8px' }}
                />
              </div>
            </div>

            <div className="m-0 p-0 mt-2">
              {error && <small className="text-danger">Please select date and time</small>}
              {dateTimeError && <small className="text-danger">Please select a future date and time</small>}
            </div>

            <div className="d-flex justify-content-end p-0 mb-0 mt-3">
              <Button
                label="Schedule"
                disabled={!customDate || !customTime || dateTimeError}
                className={`schedule-confirm-btn rounded-3 px-3 py-2 fs-6 text-white border-0 ${
                  !customDate || !customTime || dateTimeError ? '' : 'cursor-pointer'
                }`}
                onClick={() => {
                  schedule({ type: 'custom' });
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default CustomMessageScheduleOverlayPanel;
